import { formatAddress } from "@ccnote/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { CopyButton } from "../components/CopyButton";
import { Logo } from "../components/Logo";
import { API_URL, type SessionUser } from "../api";
import { exportAll } from "../export";
import { useBilling, useBuyStorage, useDeleteAccount, useEmptyTrash, useLinked, useLogout, useSenderMutations, useSenders, useStorage, useTokenMutations, useTokens, useUnlink, useWecomCode, useXRemarks } from "../queries";
import { ConfirmButton } from "../components/ConfirmButton";
import { StorageDetails } from "../components/StorageMeter";
import { daysUntil, formatBytes, formatDate, formatDays, formatMoney } from "../format";
import { Link, useLocation } from "../router";

export function Settings({ user }: { user: SessionUser }) {
  const logout = useLogout();
  return (
    <main className="settings">
      <header className="settings-header">
        <Link to="/" className="btn btn-small">
          ← Notes
        </Link>
        <Logo />
      </header>
      <h1>Settings</h1>

      <section className="panel">
        <h2>Your addresses</h2>
        <p className="muted">Send, forward or CC any of these. A category is created the first time you use it.</p>
        <table className="address-table">
          <tbody>
            <AddressRow label="Main address" address={user.address} />
            <AddressRow label="Into a category" address={formatAddress(user.username, "shopping")} />
            <AddressRow label="Same, if your mail app rejects #" address={formatAddress(user.username, "shopping", "+")} />
          </tbody>
        </table>
      </section>

      <Storage />
      <Senders />
      <Connected />
      <XRemarks />
      <Tokens />
      <ExportAll />

      <section className="panel">
        <h2>Account</h2>
        <p className="muted">
          Signed in as {user.email}
          {user.displayName ? ` (${user.displayName})` : ""}.
        </p>
        <button className="btn" onClick={() => logout.mutate()} disabled={logout.isPending}>
          Sign out
        </button>
      </section>

      <DeleteAccount username={user.username} />
    </main>
  );
}

/** Delete your own account: type the username back, then everything is removed straight away. */
function DeleteAccount({ username }: { username: string }) {
  const del = useDeleteAccount();
  const [typed, setTyped] = useState("");
  return (
    <section className="panel">
      <h2>Delete account</h2>
      <p className="muted">
        Permanently deletes your account, your address {formatAddress(username)}, and all notes, attachments and settings. This can't be undone, and
        the address can't be registered again. Export your notes first if you want to keep them.
      </p>
      <form
        className="link-row"
        onSubmit={(e) => {
          e.preventDefault();
          if (typed.trim().toLowerCase() === username) del.mutate(typed.trim());
        }}
      >
        <input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={`Type ${username} to confirm`}
          aria-label="Your username"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
        />
        <button className="btn btn-danger" disabled={typed.trim().toLowerCase() !== username || del.isPending}>
          {del.isPending ? "Deleting…" : "Delete account"}
        </button>
      </form>
      {del.isError && <p className="alert">{del.error.message}</p>}
    </section>
  );
}

function AddressRow({ label, address }: { label: string; address: string }) {
  return (
    <tr>
      <th scope="row">{label}</th>
      <td>
        <code>{address}</code>
      </td>
      <td>
        <CopyButton text={address} />
      </td>
    </tr>
  );
}

function Senders() {
  const senders = useSenders();
  const { add, remove } = useSenderMutations();
  const [address, setAddress] = useState("");
  const trusted = senders.data?.filter((s) => !s.blocked) ?? [];
  const blocked = senders.data?.filter((s) => s.blocked) ?? [];

  return (
    <section className="panel">
      <h2>Trusted senders</h2>
      <p className="muted">
        Mail from these addresses is saved straight into your notes. Mail from anyone else waits in Pending.
      </p>
      <ul className="item-list">
        {trusted.map((s) => (
          <li key={s.address}>
            <span>{s.address}</span>
            <button className="btn btn-small" onClick={() => remove.mutate(s.address)}>
              Remove
            </button>
          </li>
        ))}
      </ul>
      <form
        className="inline-form"
        onSubmit={(e) => {
          e.preventDefault();
          add.mutate(address.trim(), { onSuccess: () => setAddress("") });
        }}
      >
        <input type="email" required placeholder="name@example.com" value={address} onChange={(e) => setAddress(e.target.value)} />
        <button className="btn" disabled={add.isPending}>
          Add sender
        </button>
      </form>
      {add.isError && <p className="field-error">That doesn't look like an email address.</p>}

      {blocked.length > 0 && (
        <>
          <h3>Blocked</h3>
          <ul className="item-list">
            {blocked.map((s) => (
              <li key={s.address}>
                <span>{s.address}</span>
                <button className="btn btn-small" onClick={() => remove.mutate(s.address)}>
                  Unblock
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

/** Settings only points to the notes on X accounts: the list has its own page, as it can get long. */
function XRemarks() {
  const remarks = useXRemarks();
  const count = remarks.data?.length;
  return (
    <section className="panel" id="x-remarks">
      <h2>Notes on X accounts</h2>
      <p className="muted">
        Who is who on X: with the browser extension, click the pencil next to any name on x.com to write a note. Notes follow the account even
        if it changes its name or @handle.
      </p>
      <p>
        <Link to="/x-notes" className="btn">
          {count ? `Open ${count} ${count === 1 ? "note" : "notes"}` : "Open notes on X accounts"}
        </Link>
      </p>
    </section>
  );
}

function Tokens() {
  const tokens = useTokens();
  const { create, revoke } = useTokenMutations();
  const [name, setName] = useState("");
  const [created, setCreated] = useState<string | null>(null);

  return (
    <section className="panel">
      <h2>API tokens</h2>
      <p className="muted">For the browser extension, mobile apps and scripts. Revoke any you no longer use.</p>
      {created && (
        <div className="token-once">
          <p>
            <strong>Copy this token now.</strong> It won't be shown again.
          </p>
          <code>{created}</code>
          <CopyButton text={created} />
        </div>
      )}
      <ul className="item-list">
        {tokens.data?.map((t) => (
          <li key={t.id}>
            <span>
              {t.name}{" "}
              <span className="muted small">
                · created {new Date(t.createdAt).toLocaleDateString()}
                {t.lastUsedAt ? ` · last used ${new Date(t.lastUsedAt).toLocaleDateString()}` : " · never used"}
              </span>
            </span>
            <button className="btn btn-small btn-danger" onClick={() => revoke.mutate(t.id)}>
              Revoke
            </button>
          </li>
        ))}
      </ul>
      <form
        className="inline-form"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate(name.trim(), {
            onSuccess: (res) => {
              setCreated(res.token);
              setName("");
            },
          });
        }}
      >
        <input required maxLength={60} placeholder="e.g. Chrome on laptop" value={name} onChange={(e) => setName(e.target.value)} />
        <button className="btn" disabled={create.isPending}>
          Create token
        </button>
      </form>
    </section>
  );
}

function ExportAll() {
  const [state, setState] = useState<{ busy: boolean; count: number; done?: number; error?: string }>({ busy: false, count: 0 });
  return (
    <section className="panel">
      <h2>Export all notes</h2>
      <p className="muted">
        Download every note as Markdown files in a .zip, one folder per category. Archived notes are included; Pending, Trash and attachments
        aren't.
      </p>
      <div>
        <button
          className="btn"
          disabled={state.busy}
          onClick={async () => {
            setState({ busy: true, count: 0 });
            try {
              const done = await exportAll((count) => setState((s) => ({ ...s, count })));
              setState({ busy: false, count: done, done });
            } catch {
              setState({ busy: false, count: 0, error: "The export didn't finish. Please try again." });
            }
          }}
        >
          {state.busy ? `Preparing… ${state.count} notes` : "Download .zip"}
        </button>
      </div>
      {state.done !== undefined && <p className="muted small">Exported {state.done} notes.</p>}
      {state.error && <p className="alert">{state.error}</p>}
    </section>
  );
}

const X_RESULTS: Record<string, string> = {
  connected: "Your X account is connected.",
  cancelled: "Connecting X was cancelled.",
  expired: "That took too long. Please try again.",
  failed: "X didn't confirm your account. Please try again.",
  taken: "That X account is already connected to another ccnote account.",
  unavailable: "Connecting X isn't available yet.",
};

function Connected() {
  const { params } = useLocation();
  const result = params.get("x");
  const linked = useLinked();
  const unlink = useUnlink();
  const x = linked.data?.accounts.find((a) => a.provider === "x");
  const bot = linked.data?.x.botHandle ?? "CCNoteMe";

  return (
    <section className="panel" id="connected">
      <h2>Connected accounts</h2>
      {result && X_RESULTS[result] && <p className={result === "connected" ? "notice" : "alert"}>{X_RESULTS[result]}</p>}
      <div className="connected-row">
        <div>
          <strong>X</strong>
          <p className="muted small">
            Reply to any post with <code>@{bot}</code> (add <code>#category</code> to file it) and the post is saved to your notes. The bot
            likes your reply to confirm.
          </p>
          {x && (
            <p className="small">
              Connected as <strong>@{x.handle}</strong>
            </p>
          )}
        </div>
        {x ? (
          <button className="btn btn-small" onClick={() => unlink.mutate("x")} disabled={unlink.isPending}>
            Disconnect
          </button>
        ) : linked.data?.x.available ? (
          <a className="btn btn-primary btn-small" href={`${API_URL}/auth/x/start`}>
            Connect X
          </a>
        ) : (
          <span className="muted small">Coming soon</span>
        )}
      </div>
      <WecomRow linked={linked.data?.accounts.some((a) => a.provider === "wecom") ?? false} available={linked.data?.wecom.available ?? false} botName={linked.data?.wecom.botName ?? "ccnote"} />
    </section>
  );
}

function WecomRow({ linked, available, botName }: { linked: boolean; available: boolean; botName: string }) {
  const code = useWecomCode();
  const unlink = useUnlink();
  const qc = useQueryClient();
  // While a code is waiting to be sent in WeCom, keep checking so "Connected" appears by itself.
  useEffect(() => {
    if (!code.data || linked) return;
    const timer = setInterval(() => qc.invalidateQueries({ queryKey: ["linked"] }), 3000);
    return () => clearInterval(timer);
  }, [code.data, linked, qc]);
  const expires = code.data ? new Date(code.data.expiresAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : null;
  return (
    <div className="connected-row">
      <div>
        <strong>WeCom 企业微信</strong>
        <p className="muted small">
          In a group with the <code>{botName}</code> bot, quote a message and send <code>@{botName} #分类 标题</code>: the quoted text and images
          are saved to your notes. You can also message the bot directly.
        </p>
        {linked && <p className="small">Connected</p>}
        {code.data && !linked && (
          <div className="token-once">
            <p>
              Send this to the bot in WeCom (in a group, @ it first). The code works once, until {expires}.
            </p>
            <code>
              @{botName} 绑定 {code.data.code}
            </code>
            <CopyButton text={`@${botName} 绑定 ${code.data.code}`} />
          </div>
        )}
        {code.isError && <p className="alert">{code.error.message}</p>}
      </div>
      {linked ? (
        <button className="btn btn-small" onClick={() => unlink.mutate("wecom")} disabled={unlink.isPending}>
          Disconnect
        </button>
      ) : available ? (
        <button className="btn btn-primary btn-small" onClick={() => code.mutate()} disabled={code.isPending}>
          {code.data ? "New code" : "Get binding code"}
        </button>
      ) : (
        <span className="muted small">Coming soon</span>
      )}
    </div>
  );
}

function Storage() {
  const storage = useStorage();
  const empty = useEmptyTrash();
  const { hash } = useLocation();
  // Arriving from the sidebar meter (/settings#storage): bring this panel into view.
  useEffect(() => {
    if (hash === "#storage") document.getElementById("storage")?.scrollIntoView({ block: "start" });
  }, [hash, storage.data]);
  const usage = storage.data;
  return (
    <section className="panel" id="storage">
      <h2>Storage</h2>
      {!usage ? (
        <p className="muted">Loading…</p>
      ) : (
        <>
          <StorageDetails usage={usage} />
          <p className="muted small">
            Your limit: {formatBytes(usage.quota.base)}
            {usage.quota.purchased > 0 && ` + ${formatBytes(usage.quota.purchased)} from storage packs`}. Everything you keep counts: note text, pictures, videos, files and
            original emails. Notes in Trash count too, until you delete them for good.
            {usage.used >= usage.limit && " Your storage is full: new mail is returned to the sender and nothing new can be saved until you make room."}
          </p>
          <div className="row-actions">
            <Link to="/files" className="btn btn-small">
              Manage files
            </Link>
            {usage.trashedNotes > 0 && (
              <ConfirmButton
                label={`Empty trash (${usage.trashedNotes} ${usage.trashedNotes === 1 ? "note" : "notes"})`}
                confirmLabel={`Delete ${usage.trashedNotes} ${usage.trashedNotes === 1 ? "note" : "notes"} forever`}
                busy={empty.isPending}
                onConfirm={() => empty.mutate()}
              />
            )}
          </div>
          <BuyStorage />
        </>
      )}
    </section>
  );
}

/** Storage packs, paid through wanqu.me (WeChat Pay, Alipay, card): each lasts its days and can be renewed. */
function BuyStorage() {
  const billing = useBilling();
  const buy = useBuyStorage();
  const data = billing.data;
  if (!data || (!data.available && !data.orders.length)) return null;
  const busy = (key: string) => buy.isPending && JSON.stringify(buy.variables) === key;
  const go = (what: { packId: string } | { renewOrderId: string }) => buy.mutate(what, { onSuccess: (r) => window.location.assign(r.payUrl) });
  const renewalOf = (orderId: string) => data.orders.find((o) => o.renewsOrderId === orderId && o.status === "pending" && o.payUrl);
  return (
    <div className="buy-storage">
      {data.periods.length > 0 && (
        <>
          <h3>Your storage packs</h3>
          <ul className="my-packs">
            {data.periods.map((p) => {
              const left = daysUntil(p.expiresAt);
              const pending = renewalOf(p.orderId);
              return (
                <li key={p.orderId} className={p.active ? (left <= 30 ? "ending" : "") : "expired"}>
                  <span className="pack-size">+{formatBytes(p.bytes)}</span>
                  <span className="muted small">
                    {p.active
                      ? left <= 30
                        ? `Ends in ${left} ${left === 1 ? "day" : "days"} (${formatDate(p.expiresAt)})`
                        : `Until ${formatDate(p.expiresAt)}`
                      : `Ended ${formatDate(p.expiresAt)}`}
                  </span>
                  {data.available &&
                    (pending ? (
                      <a className="btn btn-small" href={pending.payUrl!}>
                        Continue payment
                      </a>
                    ) : (
                      <button
                        className={`btn btn-small ${p.active && left > 30 ? "" : "btn-primary"}`}
                        disabled={buy.isPending}
                        onClick={() => go({ renewOrderId: p.orderId })}
                      >
                        {busy(JSON.stringify({ renewOrderId: p.orderId })) ? "Opening…" : "Renew"}
                      </button>
                    ))}
                </li>
              );
            })}
          </ul>
          <p className="muted small">
            When a pack ends, its storage stops counting. Nothing is deleted, but if you're over your limit, ccnote stops saving new notes and files until you
            renew or make room.
          </p>
        </>
      )}

      <h3>Get more storage</h3>
      {data.available && data.packs.length > 0 ? (
        <>
          <p className="muted small">Pay with WeChat Pay, Alipay or card. Renew any time to keep it; renewing early adds to the end.</p>
          <ul className="packs">
            {data.packs.map((p) => (
              <li key={p.id}>
                <span className="pack-size">{p.label}</span>
                <span className="pack-price">
                  {formatMoney(p.amount, p.currency)} / {formatDays(p.days)}
                </span>
                <button className="btn btn-primary btn-small" disabled={buy.isPending} onClick={() => go({ packId: p.id })}>
                  {busy(JSON.stringify({ packId: p.id })) ? "Opening…" : "Buy"}
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="muted small">Buying storage isn't available right now.</p>
      )}
      {buy.isError && <p className="error">{(buy.error as Error).message}</p>}
      {data.orders.length > 0 && (
        <details className="orders-history">
          <summary className="muted small">Payment history</summary>
          <table className="orders">
            <tbody>
              {data.orders.map((o) => (
                <tr key={o.id}>
                  <td>{formatDate(o.createdAt)}</td>
                  <td>
                    +{formatBytes(o.bytes)} {o.renewsOrderId ? "renewal" : ""} · {formatDays(o.days)}
                  </td>
                  <td>{formatMoney(o.amount, o.currency)}</td>
                  <td className={`order-${o.status}`}>
                    {o.status === "pending" && o.payUrl ? (
                      <a href={o.payUrl}>Waiting for payment · continue</a>
                    ) : (
                      { paid: "Paid", refunded: "Refunded", expired: "Not paid", failed: "Failed", pending: "Waiting" }[o.status]
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}
